/**
 * Cable profiles -- group 07.
 *
 * Klotz, Cordial, AudioArt Cable, Pangea Audio, Anticables, Clear Day Audio,
 * Duelund Coherent Audio, Western Electric.
 *
 * Two very different kinds of maker sit side by side in this group. Klotz and
 * Cordial are German pro-audio houses whose catalogues state conductor
 * cross-section, screen construction and connector type, so their entries can
 * carry a real loop resistance worked out from the stated cross-section. The
 * boutique makers -- AudioArt, Anticables, Clear Day, Duelund -- publish
 * construction descriptions and nothing electrical, so their entries pass only
 * construction and let the kit derive R, L and C from gauge, material and
 * geometry. Western Electric wire is New Old Stock from a company that stopped
 * making it decades ago; nothing about it is a current manufacturer spec.
 *
 * No entry here claims a cable makes anything warmer, blacker or more open.
 * Power, USB and ethernet entries describe the build and stop there, because
 * the engine applies no signal processing to those types at all.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_07: CableProfile[] = [
  // =========================================================================
  // Klotz -- Klotz AIS GmbH, Germany. Pro-audio bulk cable and assemblies.
  // =========================================================================
  makeCable({
    mfr: 'Klotz',
    model: 'LY215',
    cableType: 'speaker',
    price: usd(55, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    resistanceOhmPerM: 0.0266,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance from the stated 2 x 1.5 mm2 class 5 stranded conductor (13.3 ohm/km per core); inductance and capacitance derived from the laid-up pair geometry.',
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'spade', 'Speakon NL2'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'bulk-cable', 'stranded'],
    notes:
      'Two 1.5 mm2 stranded copper cores laid up inside a PVC jacket. Loop resistance is about 0.027 ohm/m, so a 3 m run adds roughly 0.08 ohm in series with the loudspeaker -- about 0.09 dB of level into an 8 ohm load and around 0.2 dB into a 3 ohm minimum, plus the same divider acting on the amplifier damping factor. That is the whole of what the engine can compute for it.',
    provenanceNote:
      'Construction and conductor cross-section from the Klotz catalogue. Loop resistance follows from that cross-section; inductance and capacitance are derived from geometry, as Klotz does not state them for loudspeaker cable.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'LY225',
    cableType: 'speaker',
    price: usd(85, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    resistanceOhmPerM: 0.01596,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance from the stated 2 x 2.5 mm2 class 5 stranded conductor (7.98 ohm/km per core); inductance and capacitance derived from the laid-up pair geometry.',
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'spade', 'Speakon NL2', 'Speakon NL4'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'bulk-cable', 'stranded'],
    notes:
      'The 2.5 mm2 version of the same construction. Going from 1.5 to 2.5 mm2 cuts loop resistance from about 0.027 to about 0.016 ohm/m, which is the only difference the engine sees: a 3 m run drops from roughly 0.08 to 0.05 ohm in series with the load. Inductance is unchanged because the layup is unchanged.',
    provenanceNote:
      'Construction and conductor cross-section from the Klotz catalogue. Loop resistance follows from that cross-section; inductance and capacitance are derived from geometry, as Klotz does not state them for loudspeaker cable.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'MC5000',
    cableType: 'interconnect',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated XLR pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Double screen: conductive foil under a dense spiral copper braid',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA', 'TRS'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'balanced', 'double-screened'],
    notes:
      'Klotz\'s top studio microphone cable: a twisted 0.22 mm2 pair under a foil screen and a spiral copper braid, with a conductive layer to keep handling noise out. Used as a line interconnect the pair capacitance against a low-impedance source puts the low-pass corner far above the audio band, so the computed in-band effect is a small fraction of a decibel.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'MY206',
    cableType: 'interconnect',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated XLR pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral copper screen',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'TRS'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'balanced', 'bulk-cable'],
    notes:
      'The house workhorse: a twisted 0.22 mm2 pair under a spiral copper screen in a flexible PVC jacket. Spiral screening is chosen for flexibility and repeated coiling rather than for RF performance, where a braid does better. Electrically it is an ordinary shielded pair and the engine treats it as one.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'M1 Prime',
    cableType: 'interconnect',
    price: usd(50, 'CURRENT_RETAIL', 2024, 'Price for a single 3 m XLR female to XLR male lead.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral copper screen',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'balanced', 'assembled'],
    notes:
      'A factory assembly: MY206-class shielded pair terminated with Neutrik XLRs and strain relief. The point of the product is the termination and the assembly quality rather than the cable, which is the same shielded 0.22 mm2 pair used loose off the reel.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'M2',
    cableType: 'interconnect',
    price: usd(85, 'CURRENT_RETAIL', 2024, 'Price for a single 3 m XLR female to XLR male lead.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Foil plus spiral copper screen',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'balanced', 'assembled'],
    notes:
      'The upper assembly line, using the heavier double-screened cable and gold-contact Neutrik XLRs. Gold plating on a connector is a contact-corrosion measure, not a conduction one -- the contact resistance of a clean nickel XLR is already far below the loop resistance of the cable behind it.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'La Grange GY107',
    cableType: 'interconnect',
    price: usd(95, 'CURRENT_RETAIL', 2024, 'Price for a 6 m jack-to-jack lead.'),
    priceLengthM: 6,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 20,
    shielding: 'Dense copper braid over a semi-conductive layer',
    dielectric: 'Polyethylene',
    termination: ['TS jack', 'RCA'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'unbalanced', 'coaxial'],
    notes:
      'A thick unbalanced instrument coax: a stranded 0.5 mm2 centre in polyethylene under a semi-conductive layer and a dense braid. Capacitance is the parameter that matters for an unbalanced run, because it works against the source impedance to set a low-pass corner; a low-capacitance coax like this pushes that corner higher for a given length, and the engine reports whatever the resulting in-band deviation actually is.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'Greyhound GRG1',
    cableType: 'interconnect',
    price: usd(45, 'CURRENT_RETAIL', 2024, 'Price for a 3 m jack-to-jack lead.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Spiral copper screen',
    termination: ['TS jack'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['pro-audio', 'unbalanced', 'assembled'],
    notes:
      'The entry-level assembled instrument lead: thin spiral-screened coax with moulded jack plugs. Higher capacitance per metre than the La Grange coax above, which for a long unbalanced run out of a high source impedance is the one number worth checking.',
  }),

  makeCable({
    mfr: 'Klotz',
    model: 'RamCat RC5',
    cableType: 'ethernet',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 5 m assembly with EtherCon shells.'),
    priceLengthM: 5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 5e, 100 ohm',
    shielding: 'S/FTP, foiled pairs inside an overall braid',
    termination: ['RJ45', 'EtherCon'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f6fb0',
    tags: ['cat5e', 'shielded', 'stage-rated'],
    notes:
      'A 100 ohm Cat 5e cable built for stage use: stranded 24 AWG pairs, foil per pair inside an overall braid, and a tough flexible jacket rated for repeated coiling, normally terminated in EtherCon shells. The engine applies no signal processing to ethernet.',
  }),

  // =========================================================================
  // Cordial -- Cordial GmbH, Germany. Pro-audio bulk cable and assemblies.
  // =========================================================================
  makeCable({
    mfr: 'Cordial',
    model: 'CLS 215',
    cableType: 'speaker',
    price: usd(50, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    resistanceOhmPerM: 0.0266,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance from the stated 2 x 1.5 mm2 class 5 stranded conductor (13.3 ohm/km per core); inductance and capacitance derived from geometry.',
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'Speakon NL2'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'bulk-cable', 'stranded'],
    notes:
      'Standard 2 x 1.5 mm2 stranded loudspeaker cable in a round PVC jacket. At about 0.027 ohm/m loop, this is the gauge where run length starts to matter: 10 m adds roughly 0.27 ohm, which against a 4 ohm nominal load is a divider the engine will show as a fraction of a decibel plus a real reduction in effective damping.',
    provenanceNote:
      'Construction and cross-section from the Cordial catalogue. Loop resistance follows from that cross-section; inductance and capacitance are derived from geometry, as Cordial does not publish them for loudspeaker cable.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CLS 225',
    cableType: 'speaker',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    resistanceOhmPerM: 0.01596,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance from the stated 2 x 2.5 mm2 class 5 stranded conductor (7.98 ohm/km per core); inductance and capacitance derived from geometry.',
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'Speakon NL2', 'Speakon NL4'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'bulk-cable', 'stranded'],
    notes:
      'The 2.5 mm2 size, and the usual choice for permanent installs of ten metres and up. Loop resistance is about 0.016 ohm/m; doubling the cross-section halves the resistance and does nothing measurable to inductance, since inductance is set by the spacing between the two cores rather than by their thickness.',
    provenanceNote:
      'Construction and cross-section from the Cordial catalogue. Loop resistance follows from that cross-section; inductance and capacitance are derived from geometry, as Cordial does not publish them for loudspeaker cable.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CLS 240',
    cableType: 'speaker',
    price: usd(130, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    resistanceOhmPerM: 0.0099,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance from the stated 2 x 4.0 mm2 class 5 stranded conductor (4.95 ohm/km per core); inductance and capacitance derived from geometry.',
    dielectric: 'PVC',
    termination: ['bare', 'Speakon NL2', 'Speakon NL4'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'bulk-cable', 'heavy-gauge'],
    notes:
      'Two 4.0 mm2 cores, intended for long high-power runs. Loop resistance drops to about 0.010 ohm/m, so even a 20 m run stays near 0.2 ohm. Past this point copper cross-section has clearly diminishing returns in a domestic system, where the run is usually under 5 m and the resistance is already a small fraction of the loudspeaker impedance.',
    provenanceNote:
      'Construction and cross-section from the Cordial catalogue. Loop resistance follows from that cross-section; inductance and capacitance are derived from geometry, as Cordial does not publish them for loudspeaker cable.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CLS 425',
    cableType: 'speaker',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 10,
    resistanceOhmPerM: 0.00798,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Loop resistance for the four 2.5 mm2 cores paralleled in opposing pairs, i.e. 5.0 mm2 per leg; inductance and capacitance derived from the quad geometry.',
    dielectric: 'PVC',
    termination: ['Speakon NL4', 'bare', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['star-quad', 'pro-audio', 'bi-wire-capable'],
    notes:
      'Four 2.5 mm2 cores, sold for four-pole Speakon runs to bi-amplified boxes. Wired instead as a quad, with the cores paralleled in opposing pairs, it gives 5.0 mm2 per leg and about 0.008 ohm/m loop, and the opposed current paths cut inductance relative to a simple two-core cable of the same copper. The figures here assume that quad wiring.',
    provenanceNote:
      'Construction and cross-section from the Cordial catalogue. The quoted electrical figures assume the four cores paralleled in opposing pairs rather than used as two separate circuits.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CMK 222',
    cableType: 'interconnect',
    price: usd(50, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated XLR pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral copper screen',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'TRS', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'balanced', 'bulk-cable'],
    notes:
      'The standard German studio pair: 2 x 0.22 mm2 twisted under a spiral copper screen. In balanced use the twist is doing the work -- both conductors pick up the same interference and the receiving differential input subtracts it -- and the screen handles what the twist does not.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CIK 122',
    cableType: 'interconnect',
    price: usd(40, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated unbalanced pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Spiral copper screen over a conductive PVC layer',
    dielectric: 'PVC',
    termination: ['TS jack', 'RCA'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'unbalanced', 'coaxial'],
    notes:
      'Unbalanced instrument coax with a conductive plastic layer under the screen to suppress the triboelectric noise a moving cable generates. Used as a short line interconnect its capacitance sets a low-pass corner well outside the audio band unless the source impedance is very high.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CCM 5 FM',
    cableType: 'interconnect',
    price: usd(55, 'CURRENT_RETAIL', 2024, 'Price for a single 5 m XLR female to XLR male lead.'),
    priceLengthM: 5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral copper screen',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'balanced', 'assembled'],
    notes:
      'A finished 5 m balanced lead: CMK 222-class shielded pair with Neutrik XLRs. At 5 m the total pair capacitance is still only a few hundred picofarads, which against a normal line output impedance leaves the corner frequency in the megahertz.',
  }),

  makeCable({
    mfr: 'Cordial',
    model: 'CFM 3 VV',
    cableType: 'interconnect',
    price: usd(35, 'CURRENT_RETAIL', 2024, 'Price for a single 3 m jack-to-jack lead.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Spiral copper screen',
    termination: ['TS jack'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d64545',
    tags: ['pro-audio', 'unbalanced', 'assembled'],
    notes:
      'The assembled unbalanced lead built from CIK 122 with Neutrik jack plugs. Nothing exotic: a screened single core, soldered and strain-relieved, which is the reference point against which any expensive unbalanced interconnect should be compared.',
  }),

  // =========================================================================
  // AudioArt Cable -- United States. Direct-sale, four tiers per product.
  // =========================================================================
  makeCable({
    mfr: 'AudioArt Cable',
    model: 'SC-5 Classic',
    cableType: 'speaker',
    price: usd(240, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'multi-gauge'],
    notes:
      'The entry speaker cable in the line: multiple copper conductors of differing gauge twisted into each polarity, roughly 12 AWG equivalent per leg. That works out near 0.010 ohm/m loop, so a 2.4 m pair sits around 0.025 ohm in series with the loudspeaker.',
    provenanceNote:
      'Conductor material, gauge class and layup from the maker\'s product description. AudioArt publishes no resistance, inductance or capacitance figures, so all three are derived from gauge, material and geometry.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'SC-5SE',
    cableType: 'speaker',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'multi-gauge'],
    notes:
      'The Special Edition build: more copper per leg than the Classic and better connectors. The extra cross-section is the part with an electrical consequence, and it is a modest one -- going from roughly 12 to 11 AWG takes about a fifth off an already small series resistance.',
    provenanceNote:
      'Conductor material, gauge class and layup from the maker\'s product description; the exact gauge is inferred from the described build. No electrical figures are published, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'SC-5 e2',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'cryo-treated'],
    notes:
      'The e2 build adds cryogenic treatment of conductors and connectors to the SE geometry. Cryogenic treatment is a real metallurgical process; its effect on the bulk resistivity of annealed copper is far below the tolerance of the conductor gauge itself, so the derived electrical figures here are the same as the SE.',
    provenanceNote:
      'Construction from the maker\'s product description. The cryogenic treatment is stated by the maker; no measured change in conductivity is claimed here, and the electrical figures remain derived from gauge, material and geometry.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'Statement e2 Speaker Cable',
    cableType: 'speaker',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 10,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['flagship', 'direct-sale', 'cryo-treated'],
    notes:
      'The flagship: heavier ohno-cast copper conductors in a woven layup with PTFE insulation. The weave is the part that shows up in the model -- interleaving opposing runs cancels much of the external field and lowers loop inductance, at the cost of higher capacitance, which into a loudspeaker load is of no consequence.',
    provenanceNote:
      'Conductor material, insulation and layup from the maker\'s product description; gauge inferred from the described build. AudioArt publishes no electrical figures, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'IC-3 Classic',
    cableType: 'interconnect',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper screen',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale'],
    notes:
      'A shielded twisted pair of stranded copper with the screen tied at the source end in the unbalanced version. At around 70 pF/m, a 1 m pair presents well under 100 pF, and against a typical solid-state output impedance the resulting low-pass corner lands in the megahertz.',
    provenanceNote:
      'Conductor material, screening and layup from the maker\'s product description; gauge inferred. No electrical figures are published, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'IC-3SE',
    cableType: 'interconnect',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided copper screen',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale'],
    notes:
      'Special Edition version of the same shielded pair with heavier conductors and upgraded connectors. Series resistance in an interconnect is loading a megohm-class input, so it is electrically irrelevant here; the shield and the termination are the parts of this build that do something.',
    provenanceNote:
      'Construction from the maker\'s product description; gauge inferred. No electrical figures are published, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'IC-3 e2',
    cableType: 'interconnect',
    price: usd(550, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided copper screen',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'cryo-treated'],
    notes:
      'Cryogenically treated conductors and connectors with PTFE insulation. PTFE has a lower dielectric constant than polyethylene, which lowers pair capacitance a little for the same spacing; at one metre that moves an already inaudible corner frequency slightly further out.',
    provenanceNote:
      'Construction from the maker\'s product description; gauge inferred. No electrical figures are published, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'Statement e2 Interconnect',
    cableType: 'interconnect',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 20,
    shielding: 'Braided copper screen with a drain wire',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['flagship', 'direct-sale', 'cryo-treated'],
    notes:
      'Ohno-cast copper in a braided layup under a screen, PTFE throughout. All three of the properties the engine models are, at one metre into a normal line input, small enough that the computed frequency response deviation rounds to zero decibels. The build quality is real; the signal-path consequence is not measurable at this length.',
    provenanceNote:
      'Conductor material, insulation and layup from the maker\'s product description; gauge inferred. No electrical figures are published, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'Power 1 Classic',
    cableType: 'power',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'high-current'],
    notes:
      'A 12 AWG three-conductor cord with a moulded strain relief, rated for a 15 A branch circuit. The engine applies no signal processing to power cords, so this entry changes the chain description and the system price and leaves the audio bit-identical.',
    provenanceNote:
      'Gauge, current rating and connector types from the maker\'s product description. No electrical figures are published and none are modelled: power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'Power 1 e2',
    cableType: 'power',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Foil screen with a drain wire',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['direct-sale', 'shielded', 'cryo-treated'],
    notes:
      'Heavier conductors than the Classic, cryogenically treated, with a foil screen and drain wire and a choice of C13 or C19 outlet. A screened cord is a sensible way to keep a mains lead from acting as an antenna next to a phono stage. No signal processing is applied to power cords.',
    provenanceNote:
      'Gauge class, screening and connector options from the maker\'s product description. Power cords receive no DSP, so no electrical modelling is claimed.',
  }),

  makeCable({
    mfr: 'AudioArt Cable',
    model: 'Statement e2 Power Cable',
    cableType: 'power',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Braid over foil, drain wire to the source end',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#e07a3f',
    tags: ['flagship', 'direct-sale', 'shielded', 'high-current'],
    notes:
      'The flagship cord: 10 AWG ohno-cast copper, braided layup, foil and braid screening, rated to 20 A with C19 and 5-20 options for large amplifiers. It is a well-made piece of mains hardware and the engine applies exactly zero DSP to it, which is the honest answer rather than a disappointing one.',
    provenanceNote:
      'Conductor material, gauge, screening and connector options from the maker\'s product description. Power cords receive no DSP, so no electrical modelling is claimed.',
  }),

  // =========================================================================
  // Pangea Audio -- house brand of Audio Advisor, United States.
  // =========================================================================
  makeCable({
    mfr: 'Pangea Audio',
    model: 'AC-14',
    cableType: 'power',
    price: usd(40, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    shielding: 'Foil screen',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['budget', 'shielded'],
    notes:
      'A 14 AWG screened cord with moulded connectors, sold as a replacement for the thin unscreened lead that ships in the box with most components. Ampacity and screening are the whole story. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge, screening and connector types from the published specification. Designed for Audio Advisor in the United States and manufactured in Asia. Power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'AC-14SE MkII',
    cableType: 'power',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    shielding: 'Foil screen with a drain wire',
    dielectric: 'Polyethylene',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'shielded'],
    notes:
      'Same 14 AWG format as the AC-14 but wound from Cardas-sourced copper with a heavier jacket and screen. A stated conductor source is a supply-chain fact, not an audible one, and there is nothing further for the engine to do with it: no processing is applied to power cords.',
    provenanceNote:
      'Gauge, conductor source, screening and connector types from the published specification. Designed for Audio Advisor in the United States and manufactured in Asia. Power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'AC-9 MkII',
    cableType: 'power',
    price: usd(120, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 9,
    currentRatingA: 20,
    shielding: 'Foil screen',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['high-current', 'shielded', 'heavy-gauge'],
    notes:
      'Unusually heavy for the money: 9 AWG conductors, which is thicker than most domestic branch wiring and stiff enough to need care behind a rack. The rating is what matters -- a large power amplifier drawing high transient current is the case where cord ampacity is a genuine engineering consideration. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge, current rating, screening and connector options from the published specification. Designed for Audio Advisor in the United States and manufactured in Asia. Power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'AC-9SE MkII',
    cableType: 'power',
    price: usd(230, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 9,
    currentRatingA: 20,
    shielding: 'Foil screen with a drain wire',
    dielectric: 'Polyethylene',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'high-current', 'shielded'],
    notes:
      'The 9 AWG cord in the Cardas-copper Special Edition build, with a heavier jacket and a C19 option for amplifiers that use the larger inlet. Build description only: no signal processing is applied to power cords.',
    provenanceNote:
      'Gauge, conductor source, screening and connector options from the published specification. Designed for Audio Advisor in the United States and manufactured in Asia. Power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier Interconnect',
    cableType: 'interconnect',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper screen',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'budget'],
    notes:
      'A screened twisted pair of Cardas-sourced copper with moulded RCA or XLR ends. At one metre the pair capacitance is under 100 pF, which is the number that actually determines the loading on the source, and it is small enough that the computed response deviation is a hundredth of a decibel or less.',
    provenanceNote:
      'Conductor source, screening and termination from the published specification; gauge inferred from the described build. No electrical figures are published, so R, L and C are derived. Designed in the United States, manufactured in Asia.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier SE Interconnect',
    cableType: 'interconnect',
    price: usd(180, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided copper screen with a drain wire',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'silver-content'],
    notes:
      'The Special Edition build adds silver content to the conductor and PTFE insulation. Silver has about eight per cent lower resistivity than copper, which in a 22 AWG interconnect feeding a high-impedance input changes the series resistance by a fraction of an ohm out of an already irrelevant total.',
    provenanceNote:
      'Conductor description, insulation and termination from the published specification; gauge and the exact conductor mix are inferred. No electrical figures are published, so R, L and C are derived. Designed in the United States, manufactured in Asia.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier USB',
    cableType: 'usb',
    price: usd(60, 'CURRENT_RETAIL', 2024, 'Price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['usb-2.0', 'compliant', 'cardas-copper'],
    notes:
      'A compliant 90 ohm USB 2.0 assembly with Cardas-sourced copper, separated power and data conductors and a foil-plus-braid screen. USB audio is packetised and error-checked and an asynchronous DAC generates its own clock, so the cable either carries the data or fails to. The engine applies no signal processing here.',
    provenanceNote:
      'Conductor source, screening and standard from the published specification. Designed in the United States, manufactured in Asia. USB cables receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier SE USB',
    cableType: 'usb',
    price: usd(120, 'CURRENT_RETAIL', 2024, 'Price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Double screen: foil plus dense braid',
    dielectric: 'PTFE',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#4a90a4',
    tags: ['usb-2.0', 'compliant', 'silver-plated'],
    notes:
      'Silver-plated conductors, PTFE insulation and a double screen on the same compliant 90 ohm USB 2.0 format. Silver plating matters at USB frequencies only through skin effect on the conductor surface, and a compliant link either meets the eye-diagram requirement or it does not. No signal processing is applied to USB.',
    provenanceNote:
      'Conductor plating, insulation and screening from the published specification; gauge inferred. Designed in the United States, manufactured in Asia. USB cables receive no DSP.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier Speaker Cable',
    cableType: 'speaker',
    price: usd(200, 'CURRENT_RETAIL', 2024, 'Price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'budget'],
    notes:
      'Cardas-sourced copper at roughly 12 AWG per leg in a twisted layup. Loop resistance near 0.010 ohm/m puts a 2.5 m pair at about 0.026 ohm, which is around a hundredth of an 8 ohm load and correspondingly small in the divider the engine computes.',
    provenanceNote:
      'Conductor source and termination from the published specification; gauge inferred from the described build. No electrical figures are published, so R, L and C are derived. Designed in the United States, manufactured in Asia.',
  }),

  makeCable({
    mfr: 'Pangea Audio',
    model: 'Premier SE Speaker Cable',
    cableType: 'speaker',
    price: usd(400, 'CURRENT_RETAIL', 2024, 'Price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'PTFE',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#4a90a4',
    tags: ['cardas-copper', 'silver-content'],
    notes:
      'The Special Edition uses more conductor, added silver content and a woven layup. The weave lowers loop inductance relative to a simple twisted pair and raises capacitance, which is the standard trade and is harmless into a loudspeaker; the extra cross-section takes a little more off the series resistance.',
    provenanceNote:
      'Conductor description, insulation and layup from the published specification; gauge and conductor mix inferred. No electrical figures are published, so R, L and C are derived. Designed in the United States, manufactured in Asia.',
  }),

  // =========================================================================
  // Anticables -- Paul Speltz, United States. Solid conductors, minimal
  // insulation, no jacket.
  // =========================================================================
  makeCable({
    mfr: 'Anticables',
    model: 'Level 1 Speaker Wire',
    cableType: 'speaker',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    year: 2003,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 12,
    dielectric: 'Thin red enamel coating',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'unjacketed', 'direct-sale'],
    notes:
      'The design that started the brand: a solid 12 AWG copper conductor per polarity with nothing but a thin baked enamel coating, run as two separate wires rather than a bonded pair. Loop resistance is about 0.010 ohm/m. Widely spaced conductors give the highest inductance and the lowest capacitance of any speaker cable geometry here, which is the opposite trade to a braided cable and equally defensible into a loudspeaker load.',
    provenanceNote:
      'Gauge, conductor form and insulation from the maker\'s product description. Anticables publishes no electrical figures; R, L and C are derived from gauge, material and the widely spaced solid-conductor geometry.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 2.1 Speaker Wire',
    cableType: 'speaker',
    price: usd(320, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 12,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'The same solid 12 AWG conductor format with a heavier insulation treatment and a sleeve, and factory terminations rather than bare ends. Cross-section and spacing are unchanged, so the three modelled properties are unchanged from Level 1.',
    provenanceNote:
      'Gauge, conductor form and insulation from the maker\'s product description. No electrical figures are published; R, L and C are derived.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 3 Reference Speaker Wire',
    cableType: 'speaker',
    price: usd(750, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 11,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'The Reference build increases the copper per polarity over the Level 2.1 while keeping the solid-conductor, minimally insulated approach. More cross-section means proportionally lower loop resistance; the wide spacing keeps inductance at the high end and capacitance at the low end of the range.',
    provenanceNote:
      'Conductor form and insulation from the maker\'s product description; the gauge is inferred from the described increase over the Level 2.1 rather than stated. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 4.2 Speaker Wire',
    cableType: 'speaker',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 10,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#d43f2a',
    tags: ['flagship', 'solid-core', 'direct-sale'],
    notes:
      'The top solid-conductor speaker wire in the line, with the heaviest copper section and the most substantial terminations. At roughly 10 AWG the loop resistance falls near 0.007 ohm/m, so on a normal domestic run the series resistance is a very small fraction of the loudspeaker impedance and the divider the engine computes is correspondingly small.',
    provenanceNote:
      'Conductor form and insulation from the maker\'s product description; gauge is inferred from the described build rather than stated. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 1 Interconnect',
    cableType: 'interconnect',
    price: usd(120, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    dielectric: 'Thin enamel coating',
    termination: ['RCA'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'unshielded', 'direct-sale'],
    notes:
      'Solid copper conductors with a thin coating and no overall screen, terminated in RCA plugs. The unscreened construction gives very low capacitance -- around 45 pF/m -- which pushes the low-pass corner higher than a screened cable of the same length, and in exchange offers no protection against radiated interference in a noisy rack.',
    provenanceNote:
      'Conductor form, insulation and the absence of a screen are from the maker\'s product description; gauge is inferred. No electrical figures are published; R, L and C are derived.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 2.1 Interconnect',
    cableType: 'interconnect',
    price: usd(230, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'The same solid-conductor approach with heavier insulation, a sleeve and better plugs, and an XLR option. Balanced termination is the part of this that has a mechanism: a differential receiver rejects interference common to both conductors, which is what an unscreened pair most needs.',
    provenanceNote:
      'Conductor form and insulation from the maker\'s product description; gauge inferred. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 3 Reference Interconnect',
    cableType: 'interconnect',
    price: usd(500, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'Screen tied at the source end',
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'Reference-level build with upgraded conductors and connectors and a screen grounded at one end to avoid a ground loop through the interconnect. Adding a screen raises capacitance relative to the bare Level 1 construction; at one metre that remains far too small to produce an in-band response deviation.',
    provenanceNote:
      'Construction from the maker\'s product description; gauge and screening details are inferred. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 4.2 Interconnect',
    cableType: 'interconnect',
    price: usd(1000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 19,
    shielding: 'Screen tied at the source end',
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d43f2a',
    tags: ['flagship', 'solid-core', 'direct-sale'],
    notes:
      'The top interconnect in the line: heavier solid conductors, upgraded plugs, screened and available balanced. Series resistance in an interconnect drives a load of tens of kilohms or more, so the extra copper here is mechanical rather than electrical in effect.',
    provenanceNote:
      'Construction from the maker\'s product description; gauge is inferred rather than stated. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 1 Power Cord',
    cableType: 'power',
    price: usd(180, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 12,
    currentRatingA: 15,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'Solid 12 AWG copper conductors in the same minimally insulated style as the speaker wire, assembled with a moulded IEC and a 15 A plug. Solid conductors make for a stiff cord that holds its dressed position behind a rack. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge, conductor form and connectors from the maker\'s product description. Power cords receive no DSP, so no electrical modelling is claimed.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 2.1 Power Cord',
    cableType: 'power',
    price: usd(300, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 11,
    currentRatingA: 15,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d43f2a',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'Heavier solid conductors than the Level 1 cord with upgraded connectors and a C19 option. Build description only: no signal processing is applied to power cords, so this affects the chain listing and the system price and nothing in the audio path.',
    provenanceNote:
      'Gauge class, conductor form and connector options from the maker\'s product description. Power cords receive no DSP.',
  }),

  makeCable({
    mfr: 'Anticables',
    model: 'Level 3 Reference Power Cord',
    cableType: 'power',
    price: usd(600, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 10,
    currentRatingA: 20,
    dielectric: 'Enamel coating with an outer sleeve',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#d43f2a',
    tags: ['solid-core', 'high-current', 'direct-sale'],
    notes:
      'The Reference cord uses the heaviest solid conductors in the line with 20 A connector options for large amplifiers. Ampacity and connector rating are real specifications with real safety meaning; neither is a signal-path parameter, and the engine applies no DSP to power cords.',
    provenanceNote:
      'Conductor form and connector options from the maker\'s product description; gauge is inferred rather than stated. Power cords receive no DSP.',
  }),

  // =========================================================================
  // Clear Day Audio -- United States. Solid silver speaker cable only.
  // =========================================================================
  makeCable({
    mfr: 'Clear Day Audio',
    model: 'Single Shotgun',
    cableType: 'speaker',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'shotgun',
    gauge: 16,
    dielectric: 'Thin extruded insulation over solid silver conductors',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9c6cf',
    tags: ['solid-silver', 'small-maker', 'direct-sale'],
    notes:
      'Solid 99.99 per cent silver conductors run as one bundle per polarity. Silver resistivity is about eight per cent below copper, so this behaves like a slightly heavier copper cable of the same gauge -- around 0.024 ohm/m loop rather than 0.026. That is the entire electrical difference the material makes; everything else about silver here is cost and corrosion behaviour.',
    provenanceNote:
      'Conductor material, purity and configuration from the maker\'s product description; gauge is inferred from the described build. Clear Day publishes no electrical figures, so R, L and C are derived.',
  }),

  makeCable({
    mfr: 'Clear Day Audio',
    model: 'Double Shotgun',
    cableType: 'speaker',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'shotgun',
    gauge: 13,
    dielectric: 'Thin extruded insulation over solid silver conductors',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9c6cf',
    tags: ['solid-silver', 'small-maker', 'direct-sale'],
    notes:
      'Two of the Single Shotgun runs paralleled per polarity. Paralleling two conductors halves the loop resistance -- roughly 0.024 down to 0.012 ohm/m, the same result as dropping three AWG sizes -- and slightly lowers inductance because the current now has two adjacent paths. Doubling the silver is the reason this costs twice as much and is also the whole of its measurable effect.',
    provenanceNote:
      'Conductor material and the doubled configuration from the maker\'s product description; the effective gauge is calculated from two paralleled runs. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Clear Day Audio',
    model: 'Jumpers',
    cableType: 'speaker',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate price for a set of four 0.2 m jumpers.'),
    priceLengthM: 0.2,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'shotgun',
    gauge: 16,
    termination: ['spade', 'banana'],
    standardLengthsM: [0.15, 0.2, 0.3],
    defaultLengthM: 0.2,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9c6cf',
    tags: ['solid-silver', 'jumper', 'small-maker'],
    notes:
      'Short links to replace the metal straps between the high and low binding posts on a bi-wirable loudspeaker. At 200 mm the series resistance is about five milliohms, which is comparable to the strap being replaced; the practical argument for a jumper is contact quality at four more connections rather than the wire itself.',
    provenanceNote:
      'Configuration and material from the maker\'s product description; gauge inferred. No electrical figures are published, and at this length the derived values are dominated by connector contact resistance in any case.',
  }),

  // =========================================================================
  // Duelund Coherent Audio -- Denmark. Bulk tinned-copper wire in cotton and
  // oil-impregnated cotton, sold by the metre rather than as assemblies.
  // =========================================================================
  makeCable({
    mfr: 'Duelund Coherent Audio',
    model: 'DCA12GA',
    cableType: 'speaker',
    price: usd(520, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair built from bulk wire, unterminated.'),
    priceLengthM: 3,
    country: 'Denmark',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Oil-impregnated cotton over cotton',
    termination: ['bare', 'spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8a6d3b',
    tags: ['tinned-copper', 'cotton-insulated', 'bulk-wire'],
    notes:
      'Stranded tinned copper in cotton, impregnated with oil, in the manner of mid-century telephone-plant wire. Tin plating is a corrosion measure on the strand surface and does not change the DC path, which is copper: about 0.010 ohm/m loop at 12 AWG. Cotton and oil have a low dielectric constant, so a pair made from this has lower capacitance than a plastic-jacketed cable of the same spacing.',
    provenanceNote:
      'Sold as single-conductor bulk wire; the electrical figures assume two runs laid up as a loosely twisted pair, which is how it is normally used as speaker cable. Conductor construction and insulation are from the maker\'s description; no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Duelund Coherent Audio',
    model: 'DCA16GA',
    cableType: 'speaker',
    price: usd(260, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair built from bulk wire, unterminated.'),
    priceLengthM: 3,
    country: 'Denmark',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    dielectric: 'Oil-impregnated cotton over cotton',
    termination: ['bare', 'spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8a6d3b',
    tags: ['tinned-copper', 'cotton-insulated', 'bulk-wire'],
    notes:
      'The 16 AWG size, the most used of the range for speaker runs. Loop resistance is about 0.026 ohm/m, so on runs beyond three or four metres into a low-impedance loudspeaker the series resistance becomes large enough for the engine to show a visible divider effect; the 12 AWG version above is the fix for that.',
    provenanceNote:
      'Sold as single-conductor bulk wire; the electrical figures assume two runs laid up as a loosely twisted pair. Conductor construction and insulation are from the maker\'s description; no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Duelund Coherent Audio',
    model: 'DCA20GA',
    cableType: 'interconnect',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair built from bulk wire, unterminated.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 20,
    dielectric: 'Oil-impregnated cotton over cotton',
    termination: ['bare', 'RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8a6d3b',
    tags: ['tinned-copper', 'cotton-insulated', 'unshielded'],
    notes:
      'The 20 AWG size, used for interconnects and for internal wiring in amplifiers and loudspeakers. As an interconnect it is unscreened, so the twist and, in balanced use, the differential receiver are the only interference rejection present. Cotton and oil insulation keeps pair capacitance low.',
    provenanceNote:
      'Sold as single-conductor bulk wire; the figures assume a twisted pair with no overall screen, which is how it is normally built into an interconnect. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Duelund Coherent Audio',
    model: 'DCA26GA',
    cableType: 'interconnect',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair built from bulk wire, unterminated.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    dielectric: 'Oil-impregnated cotton over cotton',
    termination: ['bare', 'RCA'],
    balanced: false,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8a6d3b',
    tags: ['tinned-copper', 'cotton-insulated', 'fine-gauge'],
    notes:
      'The finest size in the range, thin and flexible enough for tonearm leads and low-level internal wiring. At 26 AWG the loop resistance is around 0.27 ohm/m, which is irrelevant driving a high-impedance line input but is worth knowing before anyone uses it downstream of a moving-coil cartridge.',
    provenanceNote:
      'Sold as single-conductor bulk wire; the figures assume a twisted pair. Conductor construction and insulation are from the maker\'s description; no electrical figures are published.',
  }),

  // =========================================================================
  // Western Electric -- United States. New Old Stock wire from the Bell
  // System era, not current production.
  // =========================================================================
  makeCable({
    mfr: 'Western Electric',
    model: 'WE16GA',
    cableType: 'speaker',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 3,
    year: 1955,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'cloth-jacketed'],
    notes:
      'Stranded tinned copper in rubber under a black and white braided cotton jacket, made for Bell System plant use in the middle of the last century. Electrically it is a 16 AWG copper pair: about 0.026 ohm/m loop, ordinary inductance for a laid-up pair, and low capacitance thanks to the rubber and cotton. The tin is a strand-surface corrosion measure.',
    provenanceNote:
      'New Old Stock from Western Electric wire production that ended decades ago; the present company builds vacuum tubes and does not make this. Gauge, conductor and jacket are from the wire markings and standard Bell System practice, not from any current datasheet. Price is what NOS stock trades at, not a retail price.',
  }),

  makeCable({
    mfr: 'Western Electric',
    model: 'WE18GA',
    cableType: 'speaker',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 3,
    year: 1955,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 18,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'cloth-jacketed'],
    notes:
      'The 18 AWG size of the same cloth-jacketed tinned copper wire. Loop resistance is about 0.042 ohm/m, which on anything beyond a short run into a low-impedance loudspeaker is enough for the engine to show a real level drop and a reduced effective damping factor. It is better suited to short runs or to high-impedance horn loads.',
    provenanceNote:
      'New Old Stock from mid-century Western Electric production; not current manufacture. Gauge and construction are from the wire markings and standard Bell System practice. Price reflects what NOS stock trades at.',
  }),

  makeCable({
    mfr: 'Western Electric',
    model: 'KS-13385',
    cableType: 'speaker',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 3,
    year: 1950,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 16,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'solid-core'],
    notes:
      'A Bell System KS specification for solid tinned copper wire in rubber and cotton, used as plant and loudspeaker wiring. Solid rather than stranded, so it holds a dressed shape and is less flexible; the DC resistance for a given gauge is marginally lower than stranded wire of the same nominal size, because a stranded conductor of the same AWG contains slightly less metal.',
    provenanceNote:
      'New Old Stock against a Bell System KS specification rather than a consumer product; not current manufacture. Gauge varies between production lots, and 16 AWG is the commonly encountered size. Price reflects what NOS stock trades at.',
  }),

  makeCable({
    mfr: 'Western Electric',
    model: 'WE20GA',
    cableType: 'interconnect',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 1,
    year: 1955,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 20,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'unshielded'],
    notes:
      'The 20 AWG cloth-jacketed size, used for interconnects and for point-to-point wiring in valve equipment. Unscreened, so a twisted pair into a balanced input is the sensible way to use it; single-ended in a rack full of switch-mode supplies it has no defence against radiated interference.',
    provenanceNote:
      'New Old Stock from mid-century Western Electric production; not current manufacture. Figures assume two runs laid up as a twisted pair with no screen. Price reflects what NOS stock trades at.',
  }),

  makeCable({
    mfr: 'Western Electric',
    model: 'WE22GA',
    cableType: 'interconnect',
    price: usd(75, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 1,
    year: 1955,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'RCA'],
    balanced: false,
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'unshielded'],
    notes:
      'The 22 AWG size, thin enough for chassis wiring and short interconnects. Loop resistance near 0.11 ohm/m is of no consequence driving a line input of tens of kilohms; the cotton and rubber insulation keeps pair capacitance below that of any plastic-jacketed screened cable.',
    provenanceNote:
      'New Old Stock from mid-century Western Electric production; not current manufacture. Figures assume a twisted pair with no screen. Price reflects what NOS stock trades at.',
  }),

  makeCable({
    mfr: 'Western Electric',
    model: 'WE24GA',
    cableType: 'interconnect',
    price: usd(60, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair of New Old Stock wire, unterminated.'),
    priceLengthM: 1,
    year: 1955,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'Rubber under braided cotton',
    termination: ['bare', 'RCA'],
    balanced: false,
    confidence: 'ESTIMATED',
    accent: '#9b8579',
    tags: ['tinned-copper', 'new-old-stock', 'fine-gauge'],
    notes:
      'The finest of the commonly available NOS sizes, used for tonearm leads, headshell wiring and low-level signal runs inside equipment. At about 0.17 ohm/m loop it should not be used where the source impedance is very low and the current is not, such as between a moving-coil cartridge and a step-up transformer.',
    provenanceNote:
      'New Old Stock from mid-century Western Electric production; not current manufacture. Figures assume a twisted pair with no screen. Price reflects what NOS stock trades at.',
  }),
];
